import { PropsWithChildren, useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation } from 'react-router';
import { Button, Drawer, Typography, message } from 'antd';
import { useGetIdentity, useLogout } from '@refinedev/core';
import {
  ArrowClockwiseIcon,
  ClockCounterClockwiseIcon,
  CurrencyDollarIcon,
  HouseLineIcon,
  BellRingingIcon,
  SignOutIcon,
} from '@phosphor-icons/react';
import { useIsOnline } from '@admin-panel/hooks';
import type { IERPUser } from '@admin-panel/types';
import * as Styles from './RiderAppShell.styles';
import { enableRiderPushNotifications } from './riderPushNotifications';

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
};

const getInitials = (name?: string) => {
  if (!name) return 'R';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const initials = parts
    .slice(0, 2)
    .map(part => part[0]?.toUpperCase())
    .join('');
  return initials || 'R';
};

const isStandaloneDisplay = () => {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true
  );
};

export const RiderAppShell = ({ children }: PropsWithChildren) => {
  const location = useLocation();
  const { data: identity } = useGetIdentity<IERPUser>();
  const { mutate: logout } = useLogout();
  const isOnline = useIsOnline();
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [standalone, setStandalone] = useState(isStandaloneDisplay);
  const [enablingAlerts, setEnablingAlerts] = useState(false);

  const userName = identity?.name || 'Rider';
  const isDetailScreen = /^\/(rides|returns)\//.test(location.pathname);
  const title = useMemo(() => {
    if (location.pathname.startsWith('/cash-handover')) return 'Cash handover';
    if (location.pathname.startsWith('/returns/')) return 'Return pickup';
    if (location.pathname.startsWith('/rides/')) return 'Delivery details';
    if (location.search.includes('view=history')) return 'Ride history';
    return 'Today rides';
  }, [location.pathname, location.search]);

  useEffect(() => {
    const displayModeQuery = window.matchMedia?.('(display-mode: standalone)');
    const handleBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();
      setInstallPrompt(event as BeforeInstallPromptEvent);
    };

    const handleDisplayModeChange = () => setStandalone(isStandaloneDisplay());
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    displayModeQuery?.addEventListener?.('change', handleDisplayModeChange);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      displayModeQuery?.removeEventListener?.('change', handleDisplayModeChange);
    };
  }, []);

  const handleInstall = async () => {
    if (!installPrompt) return;
    await installPrompt.prompt();
    setInstallPrompt(null);
  };

  const refreshPage = () => {
    window.location.reload();
  };

  const handleEnableAlerts = async () => {
    setEnablingAlerts(true);
    try {
      const result = await enableRiderPushNotifications();
      if (result.status === 'enabled') {
        message.success('Ride alerts enabled on this device');
      } else if (result.status === 'denied') {
        message.warning('Notifications are blocked for this browser');
      } else if (result.status === 'not_configured') {
        message.warning('Ride alerts are not configured for this environment');
      } else {
        message.warning(result.reason);
      }
    } catch (error) {
      console.warn('Could not enable rider alerts', error);
      message.error('Ride alerts could not be enabled');
    } finally {
      setEnablingAlerts(false);
    }
  };

  return (
    <Styles.AppFrame>
      <Styles.TopBar>
        <Styles.BrandBlock>
          <Styles.BrandMark aria-hidden="true">L</Styles.BrandMark>
          <div>
            <Styles.Eyebrow>Lyskraft Riders</Styles.Eyebrow>
            <Styles.Title>{title}</Styles.Title>
          </div>
        </Styles.BrandBlock>
        <Styles.ProfileButton
          type="button"
          aria-label="Open rider menu"
          onClick={() => setMenuOpen(true)}
        >
          {getInitials(userName)}
        </Styles.ProfileButton>
      </Styles.TopBar>

      {!isOnline && (
        <Styles.NetworkBanner>
          Offline. Reconnect before delivery, payment, or return actions.
        </Styles.NetworkBanner>
      )}

      {installPrompt && !standalone && !isDetailScreen && (
        <Styles.InstallCard>
          <div>
            <strong>Use like an app</strong>
            <span>Add this to your home screen for faster access.</span>
          </div>
          <Button type="primary" onClick={handleInstall}>
            Install
          </Button>
        </Styles.InstallCard>
      )}

      <Styles.Content>{children}</Styles.Content>

      <Styles.BottomNav aria-label="Rider navigation">
        <NavLink
          to="/rides"
          end
          className={() =>
            location.pathname === '/rides' && !location.search.includes('view=history')
              ? 'active'
              : undefined
          }
        >
          <HouseLineIcon size={20} weight="bold" />
          <span>Current</span>
        </NavLink>
        <NavLink
          to="/rides?view=history"
          className={() =>
            location.pathname === '/rides' && location.search.includes('view=history')
              ? 'active'
              : undefined
          }
        >
          <ClockCounterClockwiseIcon size={20} weight="bold" />
          <span>History</span>
        </NavLink>
        <NavLink
          to="/cash-handover"
          className={() => (location.pathname === '/cash-handover' ? 'active' : undefined)}
        >
          <CurrencyDollarIcon size={20} weight="bold" />
          <span>Cash</span>
        </NavLink>
        <button type="button" onClick={refreshPage}>
          <ArrowClockwiseIcon size={20} weight="bold" />
          <span>Refresh</span>
        </button>
      </Styles.BottomNav>

      <Drawer
        title="Rider menu"
        placement="bottom"
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        height="auto"
        styles={{
          content: {
            borderTopLeftRadius: 24,
            borderTopRightRadius: 24,
          },
          body: {
            padding: 20,
          },
        }}
      >
        <Styles.MenuProfile>
          <Styles.ProfileLarge>{getInitials(userName)}</Styles.ProfileLarge>
          <div>
            <Typography.Text strong>{userName}</Typography.Text>
            <Typography.Text type="secondary" style={{ display: 'block' }}>
              {identity?.email || 'Rider account'}
            </Typography.Text>
          </div>
        </Styles.MenuProfile>
        <Styles.MenuActions>
          <Button
            size="large"
            block
            icon={<BellRingingIcon />}
            loading={enablingAlerts}
            onClick={handleEnableAlerts}
          >
            Enable ride alerts
          </Button>
          <Button size="large" block onClick={refreshPage} icon={<ArrowClockwiseIcon />}>
            Refresh rides
          </Button>
          <Button
            size="large"
            block
            danger
            icon={<SignOutIcon />}
            onClick={() => logout()}
          >
            Sign out
          </Button>
        </Styles.MenuActions>
      </Drawer>
    </Styles.AppFrame>
  );
};
